<template>
  <aside
    v-show="open"
    class="coaching-call-chat"
    :aria-label="t('individualCoaching.call.chat.headline')"
  >
    <div class="coaching-call-chat-header">
      <span>{{ t('individualCoaching.call.chat.headline') }}</span>
      <v-spacer />
      <v-btn
        :icon="Icons.CLOSE"
        variant="text"
        size="small"
        :aria-label="t('common.close')"
        @click="emit('close')"
      />
    </div>
    <div
      ref="list"
      class="coaching-call-chat-list"
      role="log"
      aria-live="polite"
    >
      <p
        v-if="messages.length === 0"
        class="coaching-call-chat-empty"
      >
        {{ t('individualCoaching.call.chat.empty') }}
      </p>
      <div
        v-for="message in messages"
        :key="message.id"
        :class="['coaching-call-chat-message', message.mine ? 'coaching-call-chat-message--mine' : '']"
      >
        <div class="coaching-call-chat-meta">
          {{ message.mine ? t('individualCoaching.call.you') : message.author.name }} · {{ time(message.createdDate) }}
        </div>
        <div class="coaching-call-chat-text">
          <template
            v-for="(part, index) in parts(message.text)"
            :key="index"
          >
            <a
              v-if="part.link"
              :href="part.text"
              target="_blank"
              rel="noopener noreferrer"
            >{{ part.text }}</a>
            <template v-else>
              {{ part.text }}
            </template>
          </template>
        </div>
      </div>
    </div>
    <form
      class="coaching-call-chat-form"
      @submit.prevent="send"
    >
      <v-textarea
        v-model="draft"
        :placeholder="t('individualCoaching.call.chat.placeholder')"
        :aria-label="t('individualCoaching.call.chat.placeholder')"
        variant="solo-filled"
        flat
        rows="1"
        auto-grow
        max-rows="4"
        density="compact"
        hide-details
        rounded="lg"
        class="coaching-call-chat-input"
        @keydown.enter.exact.prevent="send"
      />
      <v-btn
        type="submit"
        color="primary"
        variant="flat"
        :icon="Icons.SEND"
        :disabled="!draft.trim()"
        :loading="sending"
        :aria-label="t('individualCoaching.messages.send')"
        class="ml-2 coaching-call-chat-send"
      />
    </form>
  </aside>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {ErrorDialog, Icons} from '@simpleforyou/client-core';
import {useApiClient} from '@/common/api/useApiClient';
import {IndividualCoachingApi} from '@/common/api/individualCoaching/IndividualCoachingApi';
import {textParts} from '@/composables/individualCoaching/coachingFormat';
import type {CallMessage} from '@/composables/individualCoaching/useCoachingCall';
import type {IUserMessage} from '@/types/components/individualCoaching/IIndividualCoaching';
//================= [VUE COMPONENTS] ==========================

const props = defineProps<{
  clientId: string;
  sessionId: string;
  open: boolean;
  /** The call's data channel: delivers instantly to the other side. */
  sendMessage: (message: CallMessage) => boolean;
  onMessage: (handler: (message: CallMessage) => void) => () => void;
}>();
const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'unread', count: number): void;
}>();

const {t} = useI18n();
const apiClient = useApiClient();

const messages = ref<IUserMessage[]>([]);
const draft = ref('');
const sending = ref(false);
const unread = ref(0);
const list = ref<HTMLElement>();

/**
 * T00MM9OMVDXS — chat inside the call. Messages are stored like any other
 * message of the room, as the thread of this session, so a link sent during
 * the call is still there afterwards. The data channel only makes them appear
 * at once on the other side.
 */
const parts = textParts;

function time(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {hour: '2-digit', minute: '2-digit'}).format(new Date(iso));
}

function scrollToEnd(): void {
  if (list.value) {
    list.value.scrollTop = list.value.scrollHeight;
  }
}

function add(message: IUserMessage): void {
  if (messages.value.some((existing) => existing.id === message.id)) {
    return;
  }
  messages.value = [...messages.value, message];
  void nextTick(scrollToEnd);
}

async function load(): Promise<void> {
  const url = IndividualCoachingApi.messages(props.clientId, {sessionId: props.sessionId, limit: '100'});
  const response = await apiClient.get<{entities: IUserMessage[]}>(url, false).catch(() => null);
  const loaded = [...(response?.data?.entities ?? [])].reverse();
  const known = new Set(loaded.map((message) => message.id));
  messages.value = [...loaded, ...messages.value.filter((message) => !known.has(message.id))];
}

async function markRead(): Promise<void> {
  unread.value = 0;
  emit('unread', 0);
  // Whoever reads along in the call gets no bundled mail about these messages.
  await apiClient.post(IndividualCoachingApi.messagesRead(props.clientId), {}).catch(() => undefined);
}

async function send(): Promise<void> {
  const text = draft.value.trim();
  if (!text || sending.value) {
    return;
  }
  sending.value = true;
  const form = new FormData();
  form.append('message', JSON.stringify({text, sessionId: props.sessionId}));
  try {
    const response = await apiClient.post<IUserMessage>(IndividualCoachingApi.messages(props.clientId), form, {headers: {'Content-Type': 'multipart/form-data'}});
    add(response.data);
    props.sendMessage({type: 'chat', message: response.data as unknown as Record<string, unknown>});
    draft.value = '';
  } catch {
    new ErrorDialog();
  } finally {
    sending.value = false;
  }
}

const unsubscribe = props.onMessage((message) => {
  if (message.type !== 'chat' || !message.message) {
    return;
  }
  // Written by the other side: "mine" there is "theirs" here.
  add({...(message.message as unknown as IUserMessage), mine: false});
  if (props.open) {
    void markRead();
  } else {
    unread.value += 1;
    emit('unread', unread.value);
  }
});

watch(() => props.open, async (open) => {
  if (open) {
    await load();
    await markRead();
    void nextTick(scrollToEnd);
  }
});

onMounted(async () => {
  if (props.open) {
    await load();
  }
});
onBeforeUnmount(unsubscribe);

defineExpose({messages, draft, unread, parts, send, load, add});
</script>

<style scoped>
.coaching-call-chat {
  position: absolute;
  /* Below the call's top bar: minimize and picture-in-picture stay reachable. */
  top: 56px;
  right: 0;
  bottom: 0;
  width: min(340px, 100%);
  display: flex;
  flex-direction: column;
  background: rgba(20, 22, 28, 0.96);
  z-index: 5;
}

.coaching-call-chat-header {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 16px;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.coaching-call-chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.coaching-call-chat-empty {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.coaching-call-chat-message {
  margin-bottom: 12px;
  max-width: 90%;
}

.coaching-call-chat-message--mine {
  margin-left: auto;
  text-align: right;
}

.coaching-call-chat-meta {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2px;
}

.coaching-call-chat-text {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
}

.coaching-call-chat-message--mine .coaching-call-chat-text {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.coaching-call-chat-text a {
  color: inherit;
  text-decoration: underline;
}

.coaching-call-chat-form {
  display: flex;
  align-items: flex-end;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.coaching-call-chat-input {
  flex: 1;
}

/* Readable on the dark panel whatever the tenant's colours are. */
.coaching-call-chat-input :deep(.v-field) {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.coaching-call-chat-input :deep(textarea::placeholder) {
  color: rgba(255, 255, 255, 0.6);
  opacity: 1;
}
</style>
